import React, { useEffect, useState } from "react";
import { NumberInput, Loader } from "@mantine/core";
import type { FieldComponentProps } from "@/pages/ClientProfile/types";
import { useProfileStore } from "@/pages/ClientProfile/store/profileStore";
import { useParams } from "react-router-dom";
import {
  parseNumberFieldValue,
  showFieldError,
} from "@utils/reviewFieldUtils.ts";

interface NumberFieldProps extends FieldComponentProps {
  mutationType?: "profile" | "ocr";
  formId?: string;
}

const NumberField: React.FC<NumberFieldProps> = ({
  commonProps,
  field,
  item,
  mutationType: _mutationType = "profile",
  formId: _formId,
}) => {
  const isMoney = item.reviewMode?.fieldType === "money";
  const { userId } = useParams();
  const updateProfileField = useProfileStore(
    (state) => state.updateProfileField
  );

  const [loadingState, setLoadingState] = useState(false);
  const [optimisticValue, setOptimisticValue] = useState<number | undefined>(
    field.value
  );

  useEffect(() => {
    setOptimisticValue(field.value);
  }, [field.value]);

  const handleChange = (value: number | string): void => {
    const numericValue = parseNumberFieldValue(value);
    setOptimisticValue(numericValue);
    field.onChange(numericValue);
  };

  const handleBlur = async (): Promise<void> => {
    field.onBlur();

    setLoadingState(true);

    const newValue =
      optimisticValue === undefined || optimisticValue === null
        ? ""
        : optimisticValue;

    try {
      await updateProfileField(userId ?? "", field.name, newValue);
      // Success notification is shown by the store method
      setLoadingState(false);
    } catch (error: any) {
      showFieldError(error, field.name);
      setOptimisticValue(field.value);
      setLoadingState(false);
    }
  };

  return (
    <NumberInput
      {...commonProps}
      {...field}
      value={optimisticValue}
      onChange={handleChange}
      onBlur={handleBlur}
      variant="filled"
      hideControls
      thousandSeparator={isMoney ? "," : undefined}
      prefix={isMoney ? "$" : undefined}
      decimalScale={isMoney ? 2 : 0}
      allowDecimal={isMoney}
      disabled={loadingState}
      rightSection={
        loadingState || commonProps.rightSection ? <Loader size="xs" /> : null
      }
    />
  );
};

export default NumberField;
